import { useCallback, useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, FileClock, Camera, Send, MessageSquare, HandshakeIcon, CalendarClock,
  DoorOpen, Hammer, RotateCcw, CheckCircle2, StickyNote, Plus, Link2, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const KIND_META = {
  first_recorded: { icon: FileClock, tone: "text-navy" },
  request_drafted: { icon: Send, tone: "text-sky-600" },
  reported: { icon: Send, tone: "text-emerald-600" },
  followup: { icon: Send, tone: "text-amber-600" },
  landlord_response: { icon: MessageSquare, tone: "text-sky-600" },
  communication: { icon: MessageSquare, tone: "text-sky-600" },
  promise: { icon: HandshakeIcon, tone: "text-violet-600" },
  appointment: { icon: CalendarClock, tone: "text-sky-600" },
  entry: { icon: DoorOpen, tone: "text-sky-600" },
  work_performed: { icon: Hammer, tone: "text-emerald-600" },
  reported_again: { icon: RotateCcw, tone: "text-red-600" },
  result_confirmed: { icon: CheckCircle2, tone: "text-emerald-600" },
  resolved: { icon: CheckCircle2, tone: "text-emerald-600" },
  note: { icon: StickyNote, tone: "text-slate-500" },
};

const EVENT_BUTTONS = [
  ["appointment", "Appointment", CalendarClock],
  ["entry", "Entry", DoorOpen],
  ["work_performed", "Work performed", Hammer],
  ["reported_again", "Reported again", RotateCcw],
  ["result_confirmed", "Confirm result", CheckCircle2],
];

const OUTCOMES = [
  ["no_outcome_recorded", "No outcome recorded"],
  ["fulfilled", "Fulfilled"],
  ["not_fulfilled", "Not fulfilled"],
  ["disputed", "Disputed"],
];

export function RepairCaseFile({ issueId, readOnly = false, onClose, onChanged }) {
  const [cf, setCf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comms, setComms] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/maintenance/${issueId}/case-file`);
      setCf(data);
      try {
        const { data: cl } = await api.get(`/properties/${data.property_id}/comm-log`);
        setComms(cl || []);
      } catch { /* comm log optional */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the case file.");
      onClose?.();
    } finally { setLoading(false); }
  }, [issueId, onClose]);

  useEffect(() => { load(); }, [load]);

  const refresh = (data) => { setCf(data); onChanged?.(); };

  const addEvent = async (kind) => {
    setBusy(true);
    try {
      const note = kind === "reported_again" ? "Problem reported again" : "";
      const { data } = await api.post(`/maintenance/${issueId}/updates`, { kind, note });
      // updates endpoint returns the enriched issue; re-pull the case file for the merged view
      await load();
      toast.success("Recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record that."); }
    finally { setBusy(false); }
  };

  const toggleComm = async (entryId) => {
    const current = (cf.linked_communications || []).map((c) => c.entry_id);
    const next = current.includes(entryId) ? current.filter((x) => x !== entryId) : [...current, entryId];
    setBusy(true);
    try {
      await api.patch(`/maintenance/${issueId}`, { linked_comm_ids: next });
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't link that."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="repair-case-file" className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileClock size={18} className="text-brand" /> Repair case file</DialogTitle>
          <DialogDescription>One continuing, factual history for this repair issue.</DialogDescription>
        </DialogHeader>

        {loading || !cf ? (
          <div className="flex items-center justify-center py-10 text-[#64748B]" data-testid="rcf-loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : (
          <div className="space-y-4 text-sm text-[#0F172A]">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-base">{cf.title}</p>
                <span data-testid="rcf-status" className="inline-flex items-center rounded-full border border-[#CBD5E1] bg-[#F1F5F9] text-[11px] font-semibold px-2 py-0.5 text-[#334155]">{cf.current_status_label}</span>
              </div>
              {cf.description && <p className="text-xs text-[#64748B] mt-1">{cf.description}</p>}
              <p className="text-[11px] text-[#94A3B8] mt-1">First recorded {fmt(cf.first_recorded)}</p>
            </div>

            {(cf.photos?.before?.length > 0 || cf.photos?.after?.length > 0) && (
              <div data-testid="rcf-photos">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5"><Camera size={12} /> Supporting photos</p>
                <div className="mt-1.5 flex gap-2 flex-wrap">
                  {[...(cf.photos.before || []), ...(cf.photos.after || [])].map((p) => (
                    <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer" className="h-14 w-14 rounded-lg overflow-hidden border border-[#E2E8F0]">
                      <img src={fileUrl(p.storage_path)} alt="" className="h-full w-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div data-testid="rcf-timeline">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Continuing history</p>
              <ol className="mt-2 space-y-2">
                {cf.timeline.map((t, i) => {
                  const meta = KIND_META[t.kind] || KIND_META.note;
                  const Icon = meta.icon;
                  return (
                    <li key={i} className="flex items-start gap-2" data-testid={`rcf-event-${t.kind}`}>
                      <span className={`mt-0.5 shrink-0 ${meta.tone}`}><Icon size={14} /></span>
                      <div className="min-w-0">
                        <p className="text-xs"><span className="font-medium">{t.label}</span> <span className="text-[#94A3B8]">· {fmt(t.at)}</span></p>
                        {t.note && <p className="text-xs text-[#64748B] break-words">{t.note}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {!readOnly && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="rcf-add-event">
                  {EVENT_BUTTONS.map(([k, label, Icon]) => (
                    <Button key={k} size="sm" variant="outline" disabled={busy} onClick={() => addEvent(k)}
                      data-testid={`rcf-event-btn-${k}`} className="rounded-lg h-8 text-xs">
                      <Icon size={13} className="mr-1" /> {label}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Promises */}
            <PromiseSection cf={cf} issueId={issueId} readOnly={readOnly} onRefresh={refresh} />

            {/* Linked communications */}
            <div data-testid="rcf-comms">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5"><MessageSquare size={12} /> Linked communications</p>
              {cf.linked_communications.length === 0 && <p className="text-xs text-[#94A3B8] mt-1">No communications linked yet.</p>}
              {cf.linked_communications.map((c) => (
                <div key={c.entry_id} className="mt-1 text-xs text-[#334155]" data-testid={`rcf-comm-${c.entry_id}`}>
                  <span className="text-[#94A3B8]">{fmt(c.occurred_at)} · {c.direction_label} · </span>{c.summary}
                </div>
              ))}
              {!readOnly && comms.length > 0 && (
                <details className="mt-2" data-testid="rcf-comm-picker">
                  <summary className="text-xs font-medium text-brand cursor-pointer inline-flex items-center gap-1"><Link2 size={12} /> Link an existing communication</summary>
                  <div className="mt-1.5 space-y-1">
                    {comms.map((c) => {
                      const linked = cf.linked_communications.some((x) => x.entry_id === c.entry_id);
                      return (
                        <label key={c.entry_id} className="flex items-start gap-2 text-xs cursor-pointer" data-testid={`rcf-comm-option-${c.entry_id}`}>
                          <input type="checkbox" checked={linked} disabled={busy} onChange={() => toggleComm(c.entry_id)} className="mt-0.5" />
                          <span className="text-[#334155]"><span className="text-[#94A3B8]">{fmt(c.occurred_at)} — </span>{c.summary}</span>
                        </label>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>

            {/* Linked charges (read-only) */}
            {cf.linked_charges.length > 0 && (
              <div data-testid="rcf-linked-charges">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5"><Receipt size={12} /> Linked deposit charge{cf.linked_charges.length > 1 ? "s" : ""}</p>
                {cf.linked_charges.map((lc) => (
                  <p key={lc.charge_id} className="mt-1 text-xs text-[#334155]" data-testid={`rcf-charge-${lc.charge_id}`}>
                    {lc.label}{lc.amount != null ? ` · $${lc.amount}` : ""} <span className="text-[#94A3B8]">(you linked this case — not a claim of cause)</span>
                  </p>
                ))}
              </div>
            )}

            <p className="text-[11px] text-[#94A3B8] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2" data-testid="rcf-disclaimer">{cf.disclaimer}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PromiseSection({ cf, issueId, readOnly, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [madeBy, setMadeBy] = useState("");
  const [text, setText] = useState("");
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!madeBy.trim() || !text.trim()) { toast.error("Add who made it and the promise text."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/maintenance/${issueId}/promises`, {
        made_by: madeBy.trim(), promise_text: text.trim(), expected_date: expected || null,
      });
      onRefresh(data); setAdding(false); setMadeBy(""); setText(""); setExpected("");
      toast.success("Promise recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record the promise."); }
    finally { setBusy(false); }
  };

  const setOutcome = async (promiseId, outcome) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/maintenance/${issueId}/promises/${promiseId}`, { outcome });
      onRefresh(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="rcf-promises">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5"><HandshakeIcon size={12} /> Recorded promises</p>
      {(cf.promises || []).length === 0 && <p className="text-xs text-[#94A3B8] mt-1">No promises recorded.</p>}
      {(cf.promises || []).map((p) => (
        <div key={p.promise_id} className="mt-2 rounded-lg border border-[#E2E8F0] p-2.5" data-testid={`rcf-promise-${p.promise_id}`}>
          <p className="text-xs text-[#334155]"><span className="text-[#94A3B8]">User says made by </span>{p.made_by}</p>
          {p.promise_text && <p className="text-xs mt-0.5">“{p.promise_text}”</p>}
          <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-[#94A3B8]">
            <span>Recorded {fmt(p.recorded_at)}</span>
            {p.expected_date && <span>Expected {fmt(p.expected_date)}</span>}
            <span data-testid={`rcf-promise-outcome-${p.promise_id}`} className="font-medium text-[#475569]">{p.outcome_label}</span>
          </div>
          {!readOnly && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {OUTCOMES.map(([v, l]) => (
                <button key={v} disabled={busy} onClick={() => setOutcome(p.promise_id, v)}
                  data-testid={`rcf-promise-set-${v}-${p.promise_id}`}
                  className={`rounded-full border px-2 py-0.5 text-[11px] ${p.outcome === v ? "border-brand bg-brand/10 text-brand font-semibold" : "border-[#E2E8F0] text-[#64748B]"}`}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {(p.history || []).length > 1 && (
            <p className="mt-1 text-[10px] text-[#94A3B8]" data-testid={`rcf-promise-history-${p.promise_id}`}>
              History: {p.history.map((h) => h.outcome).join(" → ")}
            </p>
          )}
        </div>
      ))}
      {!readOnly && (adding ? (
        <div className="mt-2 space-y-1.5 rounded-lg border border-[#E2E8F0] p-2.5" data-testid="rcf-promise-form">
          <Input data-testid="rcf-promise-madeby" value={madeBy} onChange={(e) => setMadeBy(e.target.value)} placeholder="Who do you say made this promise?" className="h-9 rounded-lg text-sm" />
          <Textarea data-testid="rcf-promise-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Exact promise (e.g. 'Will send a plumber by Friday')" className="rounded-lg text-sm" />
          <Input data-testid="rcf-promise-expected" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="h-9 rounded-lg text-sm" />
          <div className="flex gap-1.5">
            <Button size="sm" onClick={submit} disabled={busy} data-testid="rcf-promise-save" className="rounded-lg bg-brand hover:bg-brand-hover text-white text-xs">{busy ? <Loader2 size={13} className="animate-spin" /> : "Save promise"}</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)} className="rounded-lg text-xs">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="rcf-promise-add" className="mt-2 rounded-lg h-8 text-xs"><Plus size={13} className="mr-1" /> Record a promise</Button>
      ))}
    </div>
  );
}
